import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ObjectId } from 'bson';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import {
  CategorySourceTypes,
  CategoryTypes,
  EventTypes,
  ILegacyList,
  ItemStates,
  ListItem,
  StyleVariants,
} from '@site-mate/dashpivot-shared-library';

import { TemplateListsService } from 'app/apps/template-lists.service';
import { TemplateFieldBaseComponent } from 'app/form-fields/common/template/template-field-base.component';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { LogicRuleWeb } from 'app/shared/model/item.model';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { LogicRuleService } from 'app/shared/service/logic-rules.service';
import { ToastrService } from 'app/shared/service/toastr.service';

type IDryList = Pick<ILegacyList, '_id' | 'name'>;

@Component({
  selector: 'cc-app-category',
  templateUrl: './category-template.component.html',
  styleUrls: ['../../common/template/form-control.component.scss', './category-template.component.scss'],
})
export class CategoryTemplateComponent extends TemplateFieldBaseComponent implements OnInit {
  private readonly templateListsService = inject(TemplateListsService);
  private readonly destroyRef = inject(DestroyRef);
  readonly CategorySourceTypes = CategorySourceTypes;
  readonly options = [
    { value: CategoryTypes.Inline, label: 'Multiple choice' },
    { value: CategoryTypes.Dropdown, label: 'Dropdown' },
  ];

  allItems = { enabled: false, resumedAmount: 10 };
  availableLists = new Map<IDryList['_id'], IDryList>();
  unavailableLists = new Set<IDryList['_id']>();
  listItems: ListItem[] = [];
  editingItem: { id: string; value: string };
  previousValue: CategorySourceTypes;
  loading = false;
  listReferenceDeleted = false;

  constructor(
    private readonly toastrService: ToastrService,
    private readonly eventsService: EventsService,
    private readonly logicRuleService: LogicRuleService,
    private readonly confirmService: ConfirmService,
    private readonly i18nService: TmpI18NService,
    private readonly toastr: ToastrService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.previousValue = this.model.categorySource;
    this.setupLoadingState();
    this.getAvailableLists();
    this.getSelectedListItems();
  }

  private getAvailableLists(): Subscription {
    return this.templateListsService.availableLists$.pipe(filter(Boolean), take(1)).subscribe((lists) => {
      lists.forEach((list) => this.availableLists.set(list._id, list));
    });
  }

  setupLoadingState(): Subscription {
    return this.templateListsService.listIsLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((listIsLoadingMap) => {
        this.loading = listIsLoadingMap.get(this.model.reference);
      });
  }

  private getSelectedListItems(): Subscription {
    return this.templateListsService
      .getListData(this.model.reference)
      .pipe(filter(Boolean))
      .subscribe({
        next: (list) => {
          if (!this.availableLists.has(this.model.reference)) {
            this.handleUnavailableList(list);
            this.handleDeletedList(list);
          } else {
            this.listItems = this.filterListItems(list);
          }
        },
        error: (err) => {
          if (err.status === 401) {
            this.unavailableLists.add(this.model.reference);
          }
        },
      });
  }

  private handleDeletedList(list: ILegacyList) {
    if (list.state === ItemStates.Deleted) {
      this.availableLists.delete(list._id);
      this.listReferenceDeleted = true;
      this.model.reference = null;
      this.toastr.errorByKey('listInTemplateIsDeleted', {
        toastClass: 'ngx-toastr toast-long-message',
      });
    }
  }

  private handleUnavailableList = ({ _id, name }: IDryList) => {
    this.availableLists.set(_id, { _id, name });
    this.unavailableLists.add(_id);
  };

  private filterListItems(list: ILegacyList) {
    const filteredList = ListsHandler.service.filterDeployedListItems(list, this.parentId);
    return filteredList.items;
  }

  onChangeListSource(listSource: CategorySourceTypes) {
    if (this.logicRuleService.hasLogicRules(this.model)) {
      this.confirmService
        .confirm('templateListSourceChange', {
          confirmButtonText: this.i18nService.getMessage('confirmConfirm'),
        })
        .then(() => {
          this.updateListSource(listSource);
          return true;
        })
        .catch(() => {
          this.model.categorySource = this.previousValue;
        });
    } else {
      this.updateListSource(listSource);
    }
  }

  onAddLogic() {
    if (this.isManualList && this.listValuesHaveNumberId()) {
      void this.confirmService.confirm('templateLogicRuleUnavailable', {
        confirmButtonText: this.i18nService.getMessage('confirmOk'),
        showCancelButton: false,
      });
    } else {
      this.addLogicRuleCategory();
    }
  }

  private addLogicRuleCategory() {
    this.eventsService.trackEvent(EventTypes.TemplateAddLogicClicked, { Context: this.model });
    this.model.logicRules = this.logicRuleService.addLogicRule(this.model) as LogicRuleWeb[];
    this.updated.emit(this.model);
  }

  private listValuesHaveNumberId() {
    const listItems = this.model.items || [];
    return listItems.some(({ id }) => typeof id === 'number');
  }

  private get isManualList() {
    return this.model.categorySource === CategorySourceTypes.Manual;
  }

  get displayableCount() {
    return !this.allItems.enabled ? this.allItems.resumedAmount : undefined;
  }

  updateListSource(listSource: CategorySourceTypes) {
    this.model.categorySource = listSource;
    this.previousValue = this.model.categorySource;

    if (this.isManualList && this.listValuesHaveNumberId()) {
      this.model.logicRules = [];
    }

    this.updated.emit(this.model);
  }

  toggleAllItems() {
    this.allItems.enabled = !this.allItems.enabled;
  }

  addItem(categoryInput: HTMLInputElement) {
    if (categoryInput.value) {
      this.model.items.push({
        id: new ObjectId().toHexString(),
        value: categoryInput.value,
        style: this.model.defaultColor || StyleVariants.Sky,
      });
    } else {
      this.toastrService.warnByKey('inputTextBeforeAdd');
    }
    categoryInput.value = '';
    categoryInput.focus();

    this.updated.emit(this.model);
  }

  removeItem(id: string) {
    this.model.items = this.model.items.filter((i) => i.id !== id);

    this.updated.emit(this.model);
  }

  onListSelected(event: IDryList['_id']) {
    this.listReferenceDeleted = false;
    if (!event) {
      this.listItems = [];
      return;
    }

    this.templateListsService
      .getListData(event)
      .pipe(filter(Boolean), take(1))
      .subscribe((list) => {
        this.listItems = this.filterListItems(list);
        this.updated.emit(this.model);
      });
  }

  get isList() {
    return this.model.categoryType === CategoryTypes.Dropdown;
  }

  get isInline() {
    return this.model.categoryType === CategoryTypes.Inline;
  }

  onItemEdit(item: { id: string; value: string }) {
    this.editingItem = item;
  }

  onSelectOption(option: CategoryTypes) {
    this.model.categoryType = option;
  }
}
